import { TOOL_LINKS, type ToolLink } from "@/config/links";

const STORAGE_PREFIX = "bb-org-tool-links-v1";

export type OrgToolLinksStored = {
  version: 1;
  byId: Record<string, { baseUrl: string; perLocation?: Record<string, string> }>;
  /** Overrides labels/descriptions and adds custom link types (merged with `TOOL_LINKS` by id). */
  linkTypeDefs?: Array<{ id: string; label: string; description: string }>;
};

export function orgToolLinksStorageKey(orgId: string): string {
  return `${STORAGE_PREFIX}:${orgId}`;
}

export function loadOrgToolLinksStored(orgId: string | null): OrgToolLinksStored | null {
  if (!orgId) return null;
  try {
    const raw = localStorage.getItem(orgToolLinksStorageKey(orgId));
    if (!raw) return null;
    const p = JSON.parse(raw) as OrgToolLinksStored;
    if (p?.version !== 1 || typeof p.byId !== "object" || p.byId === null) return null;
    return p;
  } catch {
    return null;
  }
}

export function clearOrgToolLinksLocal(orgId: string): void {
  try {
    localStorage.removeItem(orgToolLinksStorageKey(orgId));
  } catch {
    /* ignore */
  }
}

/** Build sorted metadata for every link id (templates + custom defs + stray byId keys). */
export function getEffectiveLinkTypeMetadata(merged: OrgToolLinksStored): ToolLink[] {
  const map = new Map<string, ToolLink>();
  for (const t of TOOL_LINKS) {
    map.set(t.id, { ...t });
  }
  if (merged.linkTypeDefs?.length) {
    for (const d of merged.linkTypeDefs) {
      const prev = map.get(d.id);
      map.set(d.id, {
        id: d.id,
        label: d.label,
        description: d.description,
        baseUrl: prev?.baseUrl ?? "",
        perLocation: prev?.perLocation,
      });
    }
  }
  for (const id of Object.keys(merged.byId)) {
    if (!map.has(id)) {
      const d = merged.linkTypeDefs?.find((x) => x.id === id);
      map.set(id, {
        id,
        label: d?.label ?? id,
        description: d?.description ?? "",
        baseUrl: "",
      });
    }
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function mergeStoredWithTemplates(stored: OrgToolLinksStored | null, templates: ToolLink[]): OrgToolLinksStored {
  const allIds = new Set<string>();
  for (const t of templates) allIds.add(t.id);
  if (stored?.linkTypeDefs) {
    for (const d of stored.linkTypeDefs) allIds.add(d.id);
  }
  if (stored?.byId) {
    for (const k of Object.keys(stored.byId)) allIds.add(k);
  }

  const byId: OrgToolLinksStored["byId"] = {};
  for (const id of allIds) {
    const tmpl = templates.find((t) => t.id === id);
    const s = stored?.byId[id];
    byId[id] = {
      baseUrl: typeof s?.baseUrl === "string" && s.baseUrl.trim() ? s.baseUrl.trim() : tmpl?.baseUrl ?? "",
      perLocation:
        s?.perLocation && typeof s.perLocation === "object"
          ? { ...s.perLocation }
          : tmpl?.perLocation
            ? { ...tmpl.perLocation }
            : undefined,
    };
  }

  return {
    version: 1,
    byId,
    linkTypeDefs: stored?.linkTypeDefs?.length ? [...stored.linkTypeDefs] : undefined,
  };
}

/**
 * Merge API payload (if any), localStorage fallback, and `TOOL_LINKS` defaults.
 */
export function buildMergedState(
  apiPayload: OrgToolLinksStored | null | undefined,
  orgId: string | null
): OrgToolLinksStored {
  const local = loadOrgToolLinksStored(orgId);
  let source: OrgToolLinksStored | null = null;
  if (apiPayload === undefined) {
    source = local;
  } else if (apiPayload === null) {
    source = local;
  } else {
    source = apiPayload;
  }
  return mergeStoredWithTemplates(source, TOOL_LINKS);
}

export function toToolLinkArray(merged: OrgToolLinksStored): ToolLink[] {
  const metas = getEffectiveLinkTypeMetadata(merged);
  return metas.map((meta) => {
    const row = merged.byId[meta.id];
    return {
      ...meta,
      baseUrl: row?.baseUrl ?? meta.baseUrl,
      perLocation: row?.perLocation,
    };
  });
}

/** Sync merge for first paint (local + defaults only, before API returns). */
export function getMergedToolLinks(orgId: string | null): ToolLink[] {
  return toToolLinkArray(buildMergedState(undefined, orgId));
}

export function newCustomLinkTypeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `lt_${crypto.randomUUID().slice(0, 8)}`;
  }
  return `lt_${Date.now().toString(36)}`;
}
